import React, { useEffect, useRef, useState } from 'react';
import { openBoxNowWidget } from './boxnow';
import { boxnowNearest, boxnowDistance } from './auth';
import ClampedDesc from './ClampedDesc';
import { SITE_COUNTRY } from './fields';
// Pure classifiers live in ship-classify.js (unit-tested); re-export for existing imports.
import { isBoxnow, isPickup, isLockerPickup, classifyGroup, splitMethodGroups } from './ship-classify';
export { isPickup, classifyGroup };

const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};

// Distance to the locker: metres under 1 km, else km with one decimal (el comma).
export function formatDistance(m) {
  if (m == null || m === '' || isNaN(m)) return '';
  return m < 1000 ? `${Math.round(m)} μ.` : `${(m / 1000).toFixed(1).replace('.', ',')} χλμ.`;
}

// Left-side icons per method type.
const IconTruck = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 3h13v11H1zM14 7h4l3 3v4h-7z" /><circle cx="6" cy="18" r="2" /><circle cx="17.5" cy="18" r="2" />
  </svg>
);
const IconLocker = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="2" width="16" height="20" rx="1.5" /><line x1="4" y1="8" x2="20" y2="8" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="9" y1="5" x2="10.5" y2="5" /><line x1="9" y1="11.5" x2="10.5" y2="11.5" /><line x1="9" y1="18.5" x2="10.5" y2="18.5" />
  </svg>
);
const IconStore = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9l1.5-5h15L21 9M4 9v11h16V9M4 9h16" /><path d="M3 9a2.4 2.4 0 004.5 0 2.4 2.4 0 004.5 0 2.4 2.4 0 004.5 0 2.4 2.4 0 004.5 0" />
  </svg>
);
// OUR «Έξυπνη θυρίδα» — a parcel-locker cabinet: three doors, the middle one lit
// green with its indicator to read as "unlocked with your code, 24/7". Duotone on
// purpose (the only coloured icon in the list) so it is instantly distinguishable
// from BoxNow's line-art locker and the storefront used by courier pickup points.
// Colours come from the checkout's CSS vars, with brand-palette fallbacks.
const IconSmartLocker = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3.8" y="2.8" width="16.4" height="18.4" rx="2.2"
      fill="var(--cn-purple, #2e0c64)" fillOpacity="0.07"
      stroke="var(--cn-purple, #2e0c64)" strokeWidth="1.6" />
    <rect x="5.6" y="10.1" width="12.8" height="4.2" rx="0.9"
      fill="var(--cn-green, #00b900)" fillOpacity="0.18" />
    <path d="M3.8 9.2h16.4M3.8 15.2h16.4"
      stroke="var(--cn-purple, #2e0c64)" strokeWidth="1.4" />
    <path d="M6.7 6.1h2.3M6.7 12.2h2.3M6.7 18.3h2.3"
      stroke="var(--cn-purple, #2e0c64)" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="16.7" cy="12.2" r="1.15" fill="var(--cn-green, #00b900)" />
  </svg>
);
// OUR staffed counter — a shop with its awning and an open (green) doorway, i.e.
// "come in during opening hours and we hand it to you". Duotone to match
// IconSmartLocker above; the mono IconStore stays for courier pickup points, so our
// own two collection points are the coloured pair.
const IconStorefront = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4.6 9.4V20.2a1 1 0 0 0 1 1h12.8a1 1 0 0 0 1-1V9.4"
      fill="var(--cn-purple, #2e0c64)" fillOpacity="0.07"
      stroke="var(--cn-purple, #2e0c64)" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M3 9.4 4.7 4.3a1 1 0 0 1 .95-.7h12.7a1 1 0 0 1 .95.7L21 9.4z"
      fill="var(--cn-purple, #2e0c64)" fillOpacity="0.16"
      stroke="var(--cn-purple, #2e0c64)" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M9.6 21.2v-5.1a2.4 2.4 0 0 1 4.8 0v5.1z"
      fill="var(--cn-green, #00b900)" fillOpacity="0.18"
      stroke="var(--cn-purple, #2e0c64)" strokeWidth="1.5" strokeLinejoin="round" />
    <circle cx="13.35" cy="18.5" r="0.85" fill="var(--cn-green, #00b900)" />
  </svg>
);
export function methodIcon(id, label) {
  const l = (label || '').toLowerCase();
  if (isBoxnow(id)) return <IconLocker />;
  if (isPickup(id)) {
    // Our two own collection points: the 24/7 smart locker vs the staffed counter —
    // distinguished by instance id (isLockerPickup), not by the word "θυρίδ".
    return isLockerPickup(id) ? <IconSmartLocker /> : <IconStorefront />;
  }
  // Courier pickup-point tiers ("ACS/Γενική: Παραλαβή απο το κατάστημα") — a shop,
  // just not ours. These are couriers by identity; the label only picks the icon.
  if (l.indexOf('παραλαβ') !== -1 || l.indexOf('κατάστημ') !== -1) return <IconStore />;
  return <IconTruck />; // couriers (ACS, Γενική, …)
}

// Shipping method picker. ACS + BoxNow shown by default (ACS first); the rest
// fold under "Άλλες μέθοδοι αποστολής". BoxNow shows a locker picker inside its
// row, only while it's the selected method.
export default function ShippingMethods({ data, value, onChange, loading, dest, boxnowLocker, onBoxnowLocker }) {
  const methods = data && Array.isArray(data.methods) ? data.methods : [];
  const [expanded, setExpanded] = useState(false);
  const [lockerLoading, setLockerLoading] = useState(false);
  const autoKey = useRef('');

  const selectedId = value || data.chosen || (methods[0] && methods[0].id) || '';
  const pc = (dest && dest.postcode) || '';
  const digits = (pc.match(/\d/g) || []).join('');
  const boxnowSelected = methods.some((m) => m.id === selectedId && isBoxnow(m.id));
  // Key on the FULL destination address (street+postcode+city), so a change of
  // address — even one that keeps the same postcode — refreshes the locker.
  const addrKey = [dest && dest.address_1, dest && dest.postcode, dest && dest.city]
    .map((x) => (x || '').trim().toLowerCase()).join('|');

  // Keep the BoxNow locker in sync with the chosen address. Whenever BoxNow is
  // selected and the destination changes:
  //   • auto-picked locker → re-find the nearest locker for the new address;
  //   • manually-picked locker → keep it, but refresh its distance so the
  //     "X από τη διεύθυνσή σου" stays accurate.
  useEffect(() => {
    if (!boxnowSelected || digits.length < 3 || !dest) return;
    const haveLocker = !!(boxnowLocker && boxnowLocker.id);
    if (autoKey.current === addrKey && haveLocker) return; // already handled this address
    autoKey.current = addrKey;

    if (boxnowLocker && boxnowLocker.auto === false && haveLocker) {
      // Respect the manual choice — only recompute its distance for the new address.
      boxnowDistance({ locker_id: boxnowLocker.id, postcode: dest.postcode, city: dest.city, address1: dest.address_1 })
        .then(({ ok, data: d }) => {
          if (ok && d.distance_m != null) onBoxnowLocker({ ...boxnowLocker, distance_m: d.distance_m });
        });
      return;
    }

    setLockerLoading(true);
    boxnowNearest({ postcode: dest.postcode, city: dest.city, address1: dest.address_1 }).then(({ ok, data: d }) => {
      setLockerLoading(false);
      if (ok && d.locker && d.locker.id) onBoxnowLocker({ ...d.locker, auto: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxnowSelected, addrKey]);

  // Server-side explanation for methods that are missing cart-wide (today: over the
  // courier weight cap → pickup only, so there is no greyed courier row to speak for
  // itself). Copy comes from PHP with the live cap in it — never restated here.
  const notice = (data && data.notice) || '';

  if (loading && !methods.length) return <p className="cn-hint">Υπολογισμός μεταφορικών…</p>;
  // No methods at all: the notice (when the server sent one) is the real reason —
  // asking for an address the shopper already gave would be misleading.
  if (!methods.length) {
    return notice
      ? <p className="cn-ship-notice">{notice}</p>
      : <p className="cn-hint">Συμπλήρωσε τη διεύθυνση για να δεις τις μεθόδους αποστολής.</p>;
  }

  // Rows in view vs. folded — the split lives in ship-classify.js, where it is unit
  // tested, because getting it wrong strands a shopper on a shipping method they did
  // not want (see splitMethodGroups for the two ways that has already happened).
  const { primary, others, group: selGroup } = splitMethodGroups(methods, selectedId);
  const visible = expanded ? [...primary, ...others] : primary;
  // From store pickup the fold is the only way to a delivery, so it says so rather
  // than counting options. Every other group has the couriers already in view.
  const moreLabel = selGroup === 'pickup'
    ? 'Θέλω αποστολή στη διεύθυνσή μου'
    : `Περισσότερες επιλογές (${others.length})`;

  function openLockerPicker() {
    const bn = cfg.boxnow || {};
    // If a locker is already chosen, centre the widget on its postcode so it
    // opens on the current selection's area (the widget has no "preselect id").
    const preZip = boxnowLocker && boxnowLocker.postcode ? boxnowLocker.postcode : pc;
    const gps = boxnowLocker && boxnowLocker.postcode ? 'off' : bn.gps;
    openBoxNowWidget(
      // SITE_COUNTRY, not dest.country — each storefront serves exactly one
      // country (see fields.jsx) and it picks the widget host, so a .cy visitor
      // gets Cypriot lockers instead of a map of Greece.
      { partnerId: bn.partnerId, postcode: preZip, gps, country: SITE_COUNTRY },
      (locker) => {
        if (!onBoxnowLocker) return;
        onBoxnowLocker({ ...locker, auto: false });
        // The widget returns no coordinates — resolve the distance server-side.
        boxnowDistance({ locker_id: locker.id, postcode: dest.postcode, city: dest.city, address1: dest.address_1 })
          .then(({ ok, data }) => {
            if (ok && data.distance_m != null) onBoxnowLocker({ ...locker, auto: false, distance_m: data.distance_m });
          });
      }
    );
  }

  // Only one method and nothing folded → it's the sole option. Render it as a
  // plain confirmed row (no radio) so it doesn't look like a set of choices.
  const single = visible.length === 1 && others.length === 0;

  const renderRow = (m) => {
    const disabled = !!m.disabled;
    const selected = m.id === selectedId && !disabled;
    const boxnow = isBoxnow(m.id);
    const picked = boxnow && boxnowLocker && boxnowLocker.id;
    return (
      <div className={`cn-method-wrap ${selected ? 'is-selected' : ''} ${single ? 'is-single' : ''} ${disabled ? 'is-disabled' : ''}`} key={m.id}>
        {/* Same card structure as the payment rows (cn-method--pay): control at the
            top-left, icon inline at the start of the title so a wrapping title flows
            under it, clamped description below, price top-right. */}
        <label className="cn-method cn-method--pay cn-method--ship">
          {single
            ? <span className="cn-method__check" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg></span>
            : <input type="radio" name="cn-ship-method" checked={selected} disabled={loading || disabled} onChange={() => { if (!disabled) onChange(m.id); }} />}
          <span className="cn-method__text">
            <span className="cn-method__head">
              <span className="cn-method__icon">{methodIcon(m.id, m.label)}</span>
              <span className="cn-method__name">{m.label}</span>
            </span>
            {m.desc ? <ClampedDesc text={m.desc} /> : null}
          </span>
          {m.cost ? <span className="cn-method__cost">{m.cost}</span> : null}
        </label>

        {/* BoxNow locker — auto-picked nearest, tap to change. Its own row. */}
        {boxnow && selected && !disabled && (
          <div className="cn-boxnow">
            {picked ? (
              <button type="button" className="cn-boxnow__pick is-picked" onClick={openLockerPicker} title={boxnowLocker.address || ''}>
                <span className="cn-boxnow__ico" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg></span>
                <span className="cn-boxnow__loc">
                  <strong>{boxnowLocker.name || 'Επιλεγμένο σημείο'}</strong>
                  {boxnowLocker.address ? <span className="cn-boxnow__addr">{boxnowLocker.address}</span> : null}
                  {boxnowLocker.distance_m != null ? (
                    <span className="cn-boxnow__dist">{formatDistance(boxnowLocker.distance_m)} από τη διεύθυνσή σου</span>
                  ) : null}
                </span>
                <span className="cn-boxnow__change">Αλλαγή</span>
              </button>
            ) : (
              <button type="button" className="cn-boxnow__pick" onClick={openLockerPicker}>
                {lockerLoading ? 'Εύρεση κοντινότερου σημείου…' : 'Επιλογή σημείου παραλαβής'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`cn-ship-methods ${loading ? 'is-recalculating' : ''}`}>
      {notice && <p className="cn-ship-notice">{notice}</p>}
      <div className="cn-method-list">
        {visible.map(renderRow)}
      </div>
      {others.length > 0 && (
        <button type="button" className="cn-link cn-ship-more" onClick={() => setExpanded((v) => !v)}>
          <span className="cn-ship-more__txt">{expanded ? 'Λιγότερες επιλογές' : moreLabel}</span>
          <span className={`cn-sum__chevron ${expanded ? 'up' : ''}`} aria-hidden="true">⌃</span>
        </button>
      )}
    </div>
  );
}
